import type { OrganisationRole } from '@evw/domain';
import { Button, Card } from '@evw/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import type { AvailabilitySlot } from '../../lib/types';
import { listLocations, listServices, listStaff } from '../tenancy/api';
import {
  addOperatingHours,
  addScheduleException,
  addWorkingHours,
  assignStaffLocation,
  assignStaffService,
  createManualBooking,
  getAvailability,
  listBookings,
  listSchedulingConfiguration
} from './api';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function SchedulingPage({
  organisationId,
  role
}: {
  organisationId: string;
  role: OrganisationRole;
}) {
  const queryClient = useQueryClient();
  const editable = ['owner', 'admin', 'manager'].includes(role);
  const canBook = editable || role === 'coordinator';
  const [staffId, setStaffId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [weekday, setWeekday] = useState(1);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);

  const core = useQuery({
    queryKey: ['schedule-core', organisationId],
    queryFn: async () => {
      const [locations, staff, services] = await Promise.all([
        listLocations(organisationId),
        listStaff(organisationId),
        listServices(organisationId)
      ]);
      return { locations, staff, services };
    }
  });
  const config = useQuery({
    queryKey: ['schedule-config', organisationId],
    queryFn: () => listSchedulingConfiguration(organisationId)
  });
  const rangeStart = useMemo(() => `${date}T00:00:00Z`, [date]);
  const bookings = useQuery({
    queryKey: ['schedule-bookings', organisationId, rangeStart],
    queryFn: () => listBookings(organisationId, rangeStart)
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['schedule-config', organisationId] });
  };
  const save = useMutation({
    mutationFn: async (
      action: 'staff-location' | 'staff-service' | 'location-hours' | 'staff-hours'
    ) => {
      if (action === 'staff-location')
        return assignStaffLocation(organisationId, staffId, locationId);
      if (action === 'staff-service') return assignStaffService(organisationId, staffId, serviceId);
      if (action === 'location-hours')
        return addOperatingHours(organisationId, locationId, weekday, startTime, endTime);
      return addWorkingHours(organisationId, locationId, staffId, weekday, startTime, endTime);
    },
    onSuccess: refresh
  });
  const search = useMutation({
    mutationFn: () => {
      const localDateAnchor = new Date(`${date}T00:00:00Z`).getTime();
      return getAvailability(
        organisationId,
        locationId,
        serviceId,
        new Date(localDateAnchor - 14 * 60 * 60 * 1000).toISOString(),
        new Date(localDateAnchor + 14 * 60 * 60 * 1000).toISOString(),
        staffId || undefined
      );
    },
    onSuccess: (data) => setSlots(data.filter((slot) => slot.local_starts_at.startsWith(date)))
  });
  const book = useMutation({
    mutationFn: createManualBooking,
    onSuccess: async () => {
      setSlots([]);
      await queryClient.invalidateQueries({ queryKey: ['schedule-bookings', organisationId] });
    }
  });

  if (core.isLoading || config.isLoading) return <p>Loading scheduling…</p>;
  if (core.error || config.error)
    return <p className="error">{core.error?.message ?? config.error?.message}</p>;

  const names = new Map(core.data!.staff.map((staff) => [staff.id, staff.display_name]));
  const locationNames = new Map(
    core.data!.locations.map((location) => [location.id, location.name])
  );

  return (
    <div className="stack">
      <Card>
        <h2>Scheduling</h2>
        <p className="muted">Configure assignments and recurring local-time availability.</p>
        <div className="schedule-grid">
          <Select
            label="Staff"
            value={staffId}
            onChange={setStaffId}
            options={core.data!.staff.map((s) => [s.id, s.display_name])}
          />
          <Select
            label="Location"
            value={locationId}
            onChange={setLocationId}
            options={core.data!.locations.map((l) => [l.id, l.name])}
          />
          <Select
            label="Service"
            value={serviceId}
            onChange={setServiceId}
            options={core.data!.services.map((s) => [s.id, s.name])}
          />
        </div>
        {editable ? (
          <div className="form-actions schedule-actions">
            <Button
              disabled={!staffId || !locationId}
              onClick={() => save.mutate('staff-location')}
            >
              Assign staff to location
            </Button>
            <Button disabled={!staffId || !serviceId} onClick={() => save.mutate('staff-service')}>
              Assign service to staff
            </Button>
          </div>
        ) : null}
        {save.error ? <p className="error">{save.error.message}</p> : null}
      </Card>

      {editable ? (
        <Card>
          <h2>Regular hours</h2>
          <div className="schedule-grid">
            <label>
              Weekday
              <select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                {weekdays.map((day, index) => (
                  <option key={day} value={index + 1}>
                    {day}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Starts
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </label>
            <label>
              Ends
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </label>
          </div>
          <div className="form-actions schedule-actions">
            <Button disabled={!locationId} onClick={() => save.mutate('location-hours')}>
              Add location hours
            </Button>
            <Button disabled={!locationId || !staffId} onClick={() => save.mutate('staff-hours')}>
              Add staff hours
            </Button>
          </div>
        </Card>
      ) : null}

      {editable ? (
        <ExceptionForm
          organisationId={organisationId}
          locationId={locationId}
          staffId={staffId}
          onSaved={refresh}
        />
      ) : null}

      <Card>
        <h2>Availability and manual test booking</h2>
        <form
          className="schedule-grid"
          onSubmit={(event) => {
            event.preventDefault();
            search.mutate();
          }}
        >
          <label>
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <Button disabled={!locationId || !serviceId || search.isPending}>
            Find available times
          </Button>
        </form>
        {search.error ? <p className="error">{search.error.message}</p> : null}
        <div className="slot-list">
          {slots.map((slot) => (
            <Button
              className="button-secondary"
              key={`${slot.staff_id}-${slot.starts_at}`}
              disabled={!canBook || book.isPending}
              onClick={() => book.mutate(slot)}
            >
              {slot.local_starts_at.slice(0, 16).replace('T', ' ')} · {names.get(slot.staff_id)}
            </Button>
          ))}
          {!slots.length ? <p className="muted">No slots loaded.</p> : null}
        </div>
        <p className="muted">
          Manual bookings are explicitly confirmed without payment; online holds remain awaiting
          payment.
        </p>
      </Card>

      <Card>
        <h2>Schedule</h2>
        <div className="records">
          {bookings.data?.map((booking) => (
            <article className="record" key={booking.id}>
              <div>
                <strong>{names.get(booking.staff_id) ?? 'Staff'}</strong>
                <p>
                  {new Date(booking.starts_at).toLocaleString()} ·{' '}
                  {locationNames.get(booking.location_id)} · {booking.status}
                </p>
              </div>
            </article>
          ))}
          {!bookings.data?.length ? <p className="muted">No upcoming bookings.</p> : null}
        </div>
      </Card>

      <Card>
        <h2>Current rules and exceptions</h2>
        <p className="muted">
          {config.data!.staffLocations.filter((item) => item.is_active).length} staff/location
          assignments · {config.data!.staffServices.filter((item) => item.is_active).length}{' '}
          staff/service assignments · {config.data!.locationHours.length} location periods ·{' '}
          {config.data!.staffHours.length} staff periods
        </p>
        {config.data!.exceptions.map((item) => (
          <p key={item.id}>
            {item.kind} · {new Date(item.starts_at).toLocaleString()} · {item.reason}
          </p>
        ))}
      </Card>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
}) {
  return (
    <label>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Select…</option>
        {options.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ExceptionForm({
  organisationId,
  locationId,
  staffId,
  onSaved
}: {
  organisationId: string;
  locationId: string;
  staffId: string;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<'closure' | 'leave' | 'block'>('closure');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      addScheduleException({
        organisation_id: organisationId,
        location_id: locationId || null,
        staff_id: kind === 'closure' ? null : staffId || null,
        kind,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        reason
      }),
    onSuccess: onSaved
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }
  return (
    <Card>
      <h2>Closure, leave, or block</h2>
      <p className="muted">
        Instants are submitted with the browser's displayed UTC offset and persisted as UTC.
      </p>
      <form className="schedule-grid" onSubmit={submit}>
        <label>
          Type
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="closure">Closure</option>
            <option value="leave">Leave</option>
            <option value="block">Block</option>
          </select>
        </label>
        <label>
          Starts
          <input
            required
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </label>
        <label>
          Ends
          <input
            required
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </label>
        <label>
          Reason
          <input
            required
            minLength={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <Button disabled={!locationId || (kind !== 'closure' && !staffId) || save.isPending}>
          Add exception
        </Button>
      </form>
      {save.error ? <p className="error">{save.error.message}</p> : null}
    </Card>
  );
}
